import { Field } from '@flywire/headlessui-tailwindcss';
import { EyeIcon } from '@flywire/icons/other/EyeIcon';
import { EyeSlashIcon } from '@flywire/icons/other/EyeSlashIcon';
import { VisuallyHidden } from '@packages/ui/components/VisuallyHidden';
import { Exclamation } from 'components/FieldList/Field/Exclamation/Exclamation';
import type { InputProps } from 'hooks/useInput/useInput';
import { useInput } from 'hooks/useInput/useInput';
import { useInputAriaProps } from 'hooks/useInputAriaProps/useInputAriaProps';
import type { FunctionComponent, RefObject } from 'react';
import { useState } from 'react';
import { useTranslations } from 'utils/translations/useTranslations';

import '../Input.scss';

const TextInput: FunctionComponent<InputProps> = ({
  'aria-describedby': ariaDescribedBy,
  className,
  disabled = false,
  error,
  floatingLabel = true,
  forwardRef,
  hint = '',
  label,
  name,
  id = name,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  type = 'text',
  readOnly = false,
  required = false,
  value = '',
  ...props
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { fieldValue, handleBlur, handleChange, handleFocus } = useInput({
    disabled,
    label,
    name,
    onBlur,
    onChange,
    onFocus,
    readOnly,
    value,
  });

  const placeholderVisible = Boolean(placeholder && !fieldValue && isFocused);
  const { inputAriaProps } = useInputAriaProps({
    'aria-describedby':
      [
        ...(ariaDescribedBy ? [ariaDescribedBy] : []),
        ...(placeholderVisible ? [`${id}-placeholder`] : []),
      ].join(' ') || undefined,
    disabled,
    error,
    hint: typeof hint === 'string' ? hint : '',
    label,
    name,
    readOnly,
    required,
  });
  const i18n = useTranslations();
  const inputType =
    type === 'password' ? (isPasswordVisible ? 'text' : 'password') : type;

  return (
    <Field id={id} floatingLabel={floatingLabel} className={className}>
      <div className="relative">
        <Field.Input
          data-no-outline
          data-testid={id}
          disabled={disabled}
          name={name}
          onBlur={e => {
            setIsFocused(false);
            handleBlur(e);
          }}
          onChange={handleChange}
          onFocus={e => {
            setIsFocused(true);
            handleFocus(e);
          }}
          placeholder={placeholder ? undefined : label}
          readOnly={readOnly}
          required={required}
          type={inputType}
          value={fieldValue}
          {...props}
          {...inputAriaProps}
          ref={forwardRef as RefObject<HTMLInputElement>}
        />
        {placeholder && !fieldValue && isFocused && (
          <span
            id={`${id}-placeholder`}
            className="pointer-events-none absolute top-6 z-20 ltr:left-3 rtl:right-3 text-sm text-gray-600"
          >
            {i18n.t(placeholder)}
          </span>
        )}
        {type === 'password' && (
          <button
            type="button"
            onClick={() => setIsPasswordVisible(!isPasswordVisible)}
            className="absolute right-4 top-4 m-0 flex cursor-pointer appearance-none items-center justify-center rounded-sm bg-transparent p-0 focus:outline-none focus-visible:outline-none"
            aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
          >
            {isPasswordVisible ? (
              <EyeIcon className="size-4 bg-white text-gray-700" />
            ) : (
              <EyeSlashIcon className="size-4 bg-white text-gray-700" />
            )}
          </button>
        )}
        {label && (
          <Field.Label id={`${id}-label`}>
            {label.includes('.') ? i18n.t(label) : label}
          </Field.Label>
        )}
      </div>
      {error && type !== 'password' && (
        <div className="absolute top-3 ltr:right-3 rtl:left-3">
          <Exclamation className="h-4 w-4 text-red-600" />
        </div>
      )}
      {hint && (
        <Field.Hint className="mb-0" data-testid={`${name}-hint`}>
          {typeof hint === 'string' ? i18n.t(hint) : hint}
        </Field.Hint>
      )}
      {error && typeof error === 'string' && (
        <Field.Error className="mb-0" data-testid={`${name}-error`}>
          <VisuallyHidden as="span">{label}</VisuallyHidden>
          {i18n.t(error)}
        </Field.Error>
      )}
    </Field>
  );
};

export { TextInput };
